import { FC } from 'react';
import { SegmentedControl, Group, Switch } from '@mantine/core';
import { Gender } from '../types/data-set.types';
import { ChartType } from '../types/chart-option.types';
import { AgeRangeOption } from '../options/age-range-options';
import { useTranslation } from 'react-i18next';

// -----------------------------------------------------------------------------
interface GrowthChartControlsProps {
  selectedChartType: ChartType;
  setSelectedChartType: (type: ChartType) => void;
  selectedGender: Gender;
  setSelectedGender: (gender: Gender) => void;
  selectedOption: string;
  setSelectedOption: (option: string) => void;
  showPatientData: boolean;
  setShowPatientData: (show: boolean) => void;
  showAdjustedData: boolean;
  setShowAdjustedData: (show: boolean) => void;
  activeOptions: AgeRangeOption[];
  isWeightForLength: boolean;
}

// -----------------------------------------------------------------------------
export const GrowthChartControls: FC<GrowthChartControlsProps> = ({
  selectedChartType,
  setSelectedChartType,
  selectedGender,
  setSelectedGender,
  selectedOption,
  setSelectedOption,
  showPatientData,
  setShowPatientData,
  showAdjustedData,
  setShowAdjustedData,
  activeOptions,
  isWeightForLength,
}) => {
  const { t } = useTranslation('patient-pages');

  return (
    <>
      <Group align="flex-end" justify="space-between">
        <Group gap="md">
          <SegmentedControl
            value={selectedChartType}
            onChange={(value: string) =>
              setSelectedChartType(value as ChartType)
            }
            data={[
              { label: t('growthCharts.controls.height'), value: 'height' },
              { label: t('growthCharts.controls.weight'), value: 'weight' },
              { label: t('growthCharts.controls.bmi'), value: 'bmi' },
              {
                label: t('growthCharts.controls.headCircumference'),
                value: 'head_circumference',
              },
              {
                label: t('growthCharts.controls.weightForLength'),
                value: 'weight_for_length',
              },
            ]}
            size="sm"
          />

          <SegmentedControl
            value={selectedGender}
            onChange={(value: string) => setSelectedGender(value as Gender)}
            data={[
              { label: t('growthCharts.controls.boys'), value: 'male' },
              { label: t('growthCharts.controls.girls'), value: 'female' },
            ]}
            size="sm"
          />

          <SegmentedControl
            key={`option-control-${activeOptions.map((o) => o.id).join('-')}`}
            value={selectedOption}
            onChange={(value) => value && setSelectedOption(value)}
            data={activeOptions.map((option) => ({
              value: option.id,
              label: t(('growthCharts.ageRanges.' + option.displayName) as any),
            }))}
          />
        </Group>
      </Group>

      <Group>
        <Switch
          label={t('growthCharts.controls.showPatientData')}
          checked={showPatientData}
          onChange={(event) => setShowPatientData(event.currentTarget.checked)}
        />
        {showPatientData && !isWeightForLength && (
          <Switch
            label={t('growthCharts.controls.adjustForGestationalAge')}
            checked={showAdjustedData}
            onChange={(event) =>
              setShowAdjustedData(event.currentTarget.checked)
            }
            disabled={!showPatientData}
          />
        )}
      </Group>
    </>
  );
};
